// src/components/services/InstallerManifestService.ts
import api from './apiService';

export interface ManagedPackageItem {
  packageIdentifier: string;
  packageName: string;
  appId: string | null;
  packageVersion: string;
  lastUpdated: string | null;
  id: string;
  _ts: number;
  schemaVersion: string;
}

export interface PaginationInfo {
  pageNumber: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export interface ManagedPackagesResponse {
  data: ManagedPackageItem[];
  message: string;
  isSuccess: boolean;
  errorCode: string | null;
  pagination: PaginationInfo;
}

export interface InstallerInfo {
  architecture: string;
  installerUrl: string;
  installerBlobLocation: string | null;
  installerDownloaded: boolean | null;
  installerType: string | null;
}

export interface PackageVersionDetail {
  packageVersion: string;
  versionKey: string;
  updateScriptUrl?: string | null;
  createdAt: string;
  updatedAt: string;
  installers: InstallerInfo[];
}

export interface PackageVersionsData {
  packageIdentifier: string;
  packageName: string;
  discoveryScriptUrl?: string | null;
  versions: PackageVersionDetail[];
}

export interface PackageVersionsResponse {
  data: PackageVersionsData;
  message: string;
  isSuccess: boolean;
  errorCode: string | null;
}

export interface ResponseModel<T> {
  isSuccess: boolean;
  message: string;
  data: T;
  errorCode?: string | null;
}

export interface ManagedPackagesParams {
  pageNumber?: number;
  pageSize?: number;
}

/**
 * Fetch paginated list of Defatom-managed packages
 * @param params Pagination parameters
 * @returns Promise with paginated managed packages
 */
// src/components/services/InstallerManifestService.ts (update interface and function)

export interface ManagedPackagesParams {
  pageNumber?: number;
  pageSize?: number;
  search?: string;
}

/**
 * Fetch paginated list of Defatom-managed packages
 * @param params Pagination and search parameters
 * @returns Promise with paginated managed packages
 */
export const fetchDefatomManagedPackages = async (params: ManagedPackagesParams = {}): Promise<ManagedPackagesResponse> => {
  const queryParams = new URLSearchParams();

  if (params.pageNumber) {
    queryParams.append('pageNumber', params.pageNumber.toString());
  }
  if (params.pageSize) {
    queryParams.append('pageSize', params.pageSize.toString());
  }
  if (params.search) {
    queryParams.append('search', params.search);
  }

  const response = await api.get<ManagedPackagesResponse>(
    `/patchreposervice/InstallerManifest/defatom-managed-packages${queryParams.toString() ? `?${queryParams.toString()}` : ''}`,
  );

  return response.data;
};

/**
 * Fetch package versions with installers for a specific package
 * @param packageIdentifier The package identifier (e.g., "Airtame.Airtame")
 * @returns Promise with package versions and installer details
 */
export const fetchPackageVersionsWithInstallers = async (packageIdentifier: string): Promise<PackageVersionsResponse> => {
  const response = await api.get<PackageVersionsResponse>(
    `/patchreposervice/InstallerManifest/package-versions-with-installers/${encodeURIComponent(packageIdentifier)}`,
  );

  return response.data;
};

/**
 * Get display-friendly architecture label
 * @param arch Architecture code
 * @returns Human-readable architecture label
 */
export const getArchitectureLabel = (arch: string): string => {
  const labels: Record<string, string> = {
    x86: 'x86 (32-bit)',
    x64: 'x64 (64-bit)',
    arm: 'ARM',
    arm64: 'ARM64',
    neutral: 'Neutral',
  };
  return labels[arch.toLowerCase()] || arch;
};

/**
 * Get installer type label
 * @param type Installer type code
 * @returns Human-readable installer type
 */
export const getInstallerTypeLabel = (type: string | null): string => {
  if (!type) return 'Unknown';

  const labels: Record<string, string> = {
    msi: 'MSI',
    exe: 'EXE',
    msix: 'MSIX',
    appx: 'APPX',
    nullsoft: 'Nullsoft',
    inno: 'Inno Setup',
    wix: 'WiX',
    zip: 'ZIP',
    portable: 'Portable',
  };
  return labels[type.toLowerCase()] || type;
};

/**
 * Format file size to human-readable format
 * @param bytes File size in bytes
 * @returns Formatted file size string
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * Check if installer is downloaded to blob storage
 * @param installer Installer info object
 * @returns Boolean indicating if installer is available in blob storage
 */
export const isInstallerDownloaded = (installer: InstallerInfo): boolean => {
  return installer.installerDownloaded === true && !!installer.installerBlobLocation;
};

/**
 * Get the preferred installer URL (blob location if available, otherwise installer URL)
 * @param installer Installer info object
 * @returns Preferred installer URL
 */
export const getPreferredInstallerUrl = (installer: InstallerInfo): string => {
  return installer.installerBlobLocation || installer.installerUrl;
};

/**
 * Format date string to localized format
 * @param dateStr ISO date string
 * @returns Formatted date string
 */
export const formatDate = (dateStr?: string | null): string => {
  if (!dateStr) return 'N/A';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
};

/**
 * Get color class based on download status
 * @param installer Installer info object
 * @returns Tailwind color class
 */
export const getDownloadStatusColor = (installer: InstallerInfo): string => {
  return isInstallerDownloaded(installer) ? 'text-green-400' : 'text-yellow-400';
};

/**
 * Get download status label
 * @param installer Installer info object
 * @returns Status label
 */
export const getDownloadStatusLabel = (installer: InstallerInfo): string => {
  return isInstallerDownloaded(installer) ? 'Available in Blob' : 'External URL';
};

export interface UpdatePackageNameData {
  packageIdentifier: string;
  newPackageName: string;
  installerManifestsUpdated: number;
  softwareInventoryUpdated: boolean;
}

export interface UpdatePackageNameResponse {
  data: UpdatePackageNameData;
  message: string;
  isSuccess: boolean;
  errorCode: string | null;
}

/**
 * Update package name for a managed package
 * @param packageIdentifier The package identifier
 * @param newPackageName The new package name
 * @returns Promise with update result
 */
export const updatePackageName = async (packageIdentifier: string, newPackageName: string): Promise<UpdatePackageNameResponse> => {
  const response = await api.put<UpdatePackageNameResponse>('/patchreposervice/InstallerManifest/update-package-name', {
    packageIdentifier,
    newPackageName,
  });

  return response.data;
};

export interface UpdateManifestsResponse {
  data: any;
  message: string;
  isSuccess: boolean;
  errorCode: string | null;
}

/**
 * Trigger a winget manifest refresh.
 * @param appIds Package identifiers to update, or null to update manifests for all packages
 * @returns Promise with the update result
 */
export const updateManifests = async (appIds: string[] | null): Promise<UpdateManifestsResponse> => {
  const response = await api.post<UpdateManifestsResponse>('/patchreposervice/wingetapi/update-manifests', {
    appIds,
  });

  return response.data;
};

/**
 * Upload discovery script for a package
 * @param packageName The package name
 * @param file The script file to upload
 * @returns Promise with upload result
 */
export const uploadDiscoveryScript = async (packageName: string, file: File): Promise<ResponseModel<any>> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post<ResponseModel<any>>(
    `/patchreposervice/InstallerManifest/discovery-script/${encodeURIComponent(packageName)}`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );

  return response.data;
};

/**
 * Upload update script for a package version
 * @param packageName The package name
 * @param version The package version
 * @param file The script file to upload
 * @returns Promise with upload result
 */
export const uploadUpdateScript = async (packageName: string, version: string, file: File): Promise<ResponseModel<any>> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post<ResponseModel<any>>(
    `/patchreposervice/InstallerManifest/update-script/${encodeURIComponent(packageName)}/${encodeURIComponent(version)}`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );

  return response.data;
};
